/**
 * Pure activity list filtering (no store side effects).
 *
 * @typedef {{
 *   profile_ids: number[];
 *   space_id: number | null;
 *   board_id: number | null;
 *   days: number | null;
 *   conversation_types: string[];
 * }} ActivityConversationFilters
 */

/**
 * @param {unknown[]} conversations
 * @param {ActivityConversationFilters} filters
 * @returns {unknown[]}
 */
export function filterActivityConversationsByTypes(conversations, filters) {
    return conversations.filter((conversation) => {
        if (filters.conversation_types.includes("dm") && conversation.isDm()) {
            return true;
        }

        if (
            filters.conversation_types.includes("group") &&
            conversation.isGroup() &&
            !conversation?.state?.is_one_off
        ) {
            return true;
        }

        if (
            filters.conversation_types.includes("task") &&
            conversation.isItem()
        ) {
            return true;
        }

        if (
            filters.conversation_types.includes("board") &&
            conversation.isBoard()
        ) {
            return true;
        }

        if (
            filters.conversation_types.includes("topics") &&
            conversation.isGroup() &&
            conversation?.state?.is_one_off
        ) {
            return true;
        }

        return false;
    });
}

/**
 * @param {unknown[]} conversations
 * @param {ActivityConversationFilters} filters
 * @returns {unknown[]}
 */
export function filterActivityConversationsWithUser(conversations, filters) {
    const selected_profile_ids = new Set(filters.profile_ids);

    return conversations.filter((conv) =>
        conv.participants.some(
            (user) =>
                user.is_removed == false &&
                selected_profile_ids.has(user.twilio_identity),
        ),
    );
}

/**
 * @param {unknown[]} handlers
 * @param {ActivityConversationFilters} filters
 * @returns {unknown[]}
 */
export function filterActivityConversations(handlers, filters) {
    let conversations = handlers;

    if (filters.space_id) {
        conversations = conversations.filter(
            (item) => item.space_id === filters.space_id,
        );
    }

    if (filters.board_id) {
        conversations = conversations.filter(
            (item) => item.board_id === filters.board_id,
        );
    }

    if (filters.conversation_types.length > 0) {
        conversations = filterActivityConversationsByTypes(
            conversations,
            filters,
        );
    }

    const seenIds = new Set();
    conversations = conversations.filter((conv) => {
        const convId = conv.id || conv.sid;
        if (seenIds.has(convId) || conv.isFake()) {
            return false;
        }
        seenIds.add(convId);
        return true;
    });

    if (filters.profile_ids.length > 0) {
        conversations = filterActivityConversationsWithUser(
            conversations,
            filters,
        );
    }

    return conversations;
}
